import { useCallback, useState } from 'react';
import { Button } from "@/components/ui/button";
import commonService from "@/services/common.service.ts";
import { CommonService } from "@/services";
import { XIcon, AlertCircle, Clock, Users, Zap } from 'lucide-react';
import * as React from 'react';
import {
    Dialog,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogContentNoClose,
} from "@/components/ui/dialog";
import { Misc } from '@/constants';
import './ErrorDisplayCardComponent.scss';
import { usePreviewErrors, ErrorMessage } from '@/hooks/usePreviewErrors';




export default React.memo(function ErrorDisplayCard() {
    const {
        errors,
        isVisible,
        aiConcurrencyIssue,
        setAiConcurrencyIssue,
        advancedModeIssue,
        setAdvancedModeIssue,
        clearErrors,
        constructErrorString,
        projectId
    } = usePreviewErrors();

    const [isCopied, setIsCopied] = useState(false);

    // Copy error details to clipboard
    const copyErrorDetails = () => {
        const formattedErrors = constructErrorString(errors);
        navigator.clipboard.writeText(formattedErrors)
            .then(() => {
                setIsCopied(true);
                setTimeout(() => setIsCopied(false), 2000);
            })
            .catch((err: any) => console.error('Failed to copy errors:', err));
    };

    // Handle sending errors to another component
    const handleSendErrors = useCallback((errorList: ErrorMessage[]) => {
        // Construct the error string with error descriptions
        const errorString = "Fix the following errors:\n\n";

        // Add error descriptions for each error
        const errorDescriptions = `<error_description>${constructErrorString(errorList) || 'Unknown error'}</error_description>`

        const fullMessage = errorString + '\n\n' + errorDescriptions;

        clearErrors();

        commonService._communications.sendMessageSubject.next({ message: fullMessage })

    }, [constructErrorString, clearErrors])

    // Handle closing AI concurrency dialog
    const handleCloseAiConcurrencyDialog = useCallback(() => {
        setAiConcurrencyIssue(false);
    }, [setAiConcurrencyIssue]);

    // Handle retry after waiting
    const handleRetryAfterWait = useCallback(() => {
        setAiConcurrencyIssue(false);
        CommonService._communications.retryChatMessageSubject.next({ retryMessage: true });
    }, [setAiConcurrencyIssue]);

    // Handle closing advanced mode dialog
    const handleCloseAdvancedModeDialog = useCallback(() => {
        setAdvancedModeIssue(false);
    }, [setAdvancedModeIssue]);

    // Handle switching to normal mode
    const handleSwitchToNormalMode = useCallback(() => {
        setAdvancedModeIssue(false);
        // Turn off advanced mode in localStorage for this project
        if (projectId) {
            localStorage.setItem(`${Misc.LOCAL_STORAGE_ADVANCED_MODE_PREFIX}${projectId}`, 'false');
        }
        // Reload the page to apply the change
        window.location.reload();
    }, [projectId, setAdvancedModeIssue]);



    // Check if we should show the AI concurrency dialog
    const shouldShowAiConcurrencyDialog = () => {
        return aiConcurrencyIssue;
    };

    // Filter out Supabase errors for the regular error display
    const nonSupabaseErrors = errors.filter((e) => e.type !== 'supabase_connection_issue');

    // Network errors are stored in Redux but NOT displayed to user
    // Only show regular (non-Supabase, non-network) errors
    const shouldShowErrorCard = nonSupabaseErrors.length > 0 && isVisible;

    return (
        <>
            {/* AI Concurrency Error Dialog */}
            <Dialog
                open={shouldShowAiConcurrencyDialog()}
                onOpenChange={handleCloseAiConcurrencyDialog}
            >
                <DialogContentNoClose className="ai-concurrency-dialog">
                    <DialogHeader>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-3 rounded-full bg-orange-500/10">
                                <Users className="w-6 h-6 text-orange-500" />
                            </div>
                            <DialogTitle className="text-xl">
                                AI is Running at Full Capacity
                            </DialogTitle>
                        </div>

                        <DialogDescription className="text-base leading-relaxed pt-2">
                            Your request is in the queue.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="my-4 p-4 rounded-lg bg-orange-500/5 border border-orange-500/20">
                        <div className="flex gap-2">
                            <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    The AI is currently running at full capacity.
                                    Please come back after <strong>5 minutes</strong>,
                                    refresh the page, and check again.
                                </p>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button
                            onClick={handleCloseAiConcurrencyDialog}
                            variant="outline"
                            className="w-full sm:w-auto"
                            size="lg"
                        >
                            Close
                        </Button>

                        <Button
                            onClick={handleRetryAfterWait}
                            className="w-full sm:w-auto"
                            size="lg"
                        >
                            <Clock className="w-4 h-4 mr-2" />
                            Refresh & Check
                        </Button>
                    </DialogFooter>
                </DialogContentNoClose>
            </Dialog>




            {/* Advanced Mode Error Dialog */}
            <Dialog open={advancedModeIssue} onOpenChange={handleCloseAdvancedModeDialog}>
                <DialogContentNoClose className="advanced-mode-error-dialog">
                    <DialogHeader>
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-3 rounded-full bg-purple-500/10">
                                <Zap className="w-6 h-6 text-purple-500" />
                            </div>
                            <DialogTitle className="text-xl">Advanced Mode Unavailable</DialogTitle>
                        </div>
                        <DialogDescription className="text-base leading-relaxed pt-2">
                            Advanced mode is currently unavailable.
                            This may be due to high demand or temporary service issues.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="my-4 p-4 rounded-lg bg-purple-500/5 border border-purple-500/20">
                        <div className="flex gap-2 mb-2">
                            <AlertCircle className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                            <div>
                                <h4 className="font-semibold text-sm mb-1">What can you do?</h4>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    You can switch to normal mode to continue building your application.
                                    Normal mode uses a different AI service that is currently available.
                                </p>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button
                            onClick={handleCloseAdvancedModeDialog}
                            variant="outline"
                            className="w-full sm:w-auto"
                            size="lg"
                        >
                            Close
                        </Button>
                        <Button
                            onClick={handleSwitchToNormalMode}
                            className="w-full sm:w-auto"
                            size="lg"
                        >
                            <Zap className="w-4 h-4 mr-2" />
                            Switch to Normal Mode
                        </Button>
                    </DialogFooter>
                </DialogContentNoClose>
            </Dialog>


            {/* Regular Error Display Card - only show non-Supabase errors */}
            {shouldShowErrorCard && (
                <div className="error-display-card">
                    <div className="error-display-card__container">
                        {/* Header */}
                        <div className="error-display-card__header">
                            <div className="error-display-card__header-left">
                                <svg
                                    className="error-display-card__header-icon"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 
               1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 
               0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    />
                                </svg>
                                <span className="error-display-card__header-title">
                                    Errors ({nonSupabaseErrors.length})
                                </span>
                            </div>
                            <div className="error-display-card__header-actions">
                                <Button
                                    className="error-display-card__button--clear"
                                    onClick={clearErrors}
                                    title="Clear All"
                                    prefixIcon={<XIcon className="w-4 h-4" />}
                                    variant="ghost"
                                >
                                    Clear
                                </Button>
                            </div>
                        </div>

                        {/* Error content */}
                        <div className="error-display-card__content">
                            <div className="error-display-card__content-scroll">
                                {nonSupabaseErrors.map((error) => (
                                    <div
                                        key={error.error_id}
                                        className="error-display-card__error-item"
                                    >
                                        <div className="error-display-card__error-item-timestamp">
                                            {error.error_timestamp}
                                        </div>
                                        {error.error_str}
                                    </div>
                                ))}
                            </div>

                            {/* Footer with action buttons */}
                            <div className="error-display-card__footer">
                                <Button
                                    onClick={copyErrorDetails}
                                    className="error-display-card__button--copy"
                                >
                                    {isCopied ? (
                                        <>
                                            <svg
                                                className="error-display-card__button-icon"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                            Copied!
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                className="error-display-card__button-icon"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M8 5H6a2 2 0 00-2 2v12a2 2 
                     0 002 2h10a2 2 0 002-2v-1M8 5a2 
                     2 0 002 2h2a2 2 0 002-2M8 5a2 
                     2 0 012-2h2a2 2 0 012 2m0 
                     0h2a2 2 0 012 2v3m2 4H10m0 
                     0l3-3m-3 3l3 3"
                                                />
                                            </svg>
                                            Copy
                                        </>
                                    )}
                                </Button>

                                <Button
                                    onClick={() => {
                                        // Track fix error button click
                                        CommonService.handleMixpanelTrack("Fix Error Button Clicked", {
                                            error_count: nonSupabaseErrors.length,
                                            project_id: projectId,
                                            timestamp: new Date().toISOString()
                                        });
                                        handleSendErrors(nonSupabaseErrors);
                                    }}
                                    className="error-display-card__button--action"
                                >
                                    Fix Error
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
});
